import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../../lib/api';

const DAYS = [
  { d: '1', label: 'Monday' },
  { d: '2', label: 'Tuesday' },
  { d: '3', label: 'Wednesday' },
  { d: '4', label: 'Thursday' },
  { d: '5', label: 'Friday' },
  { d: '6', label: 'Saturday' },
  { d: '0', label: 'Sunday' },
];

const defaultStore = {
  name: '', phone: '', phone_raw: '', whatsapp: '', address: '', email: '',
  hours: { '0': '', '1': '', '2': '', '3': '', '4': '', '5': '', '6': '' },
};

export default function AdminSettings() {
  const [store, setStore] = useState(defaultStore);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Password change state
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');

  useEffect(() => {
    api.get('/store').then(r => {
      setStore({ ...defaultStore, ...r.data, hours: { ...defaultStore.hours, ...(r.data?.hours || {}) } });
    }).catch(e => setErr(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const payload = { ...store };
      delete payload.key; delete payload.updated_at;
      await api.patch('/admin/store', payload);
      setMsg('Saved.');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr(''); setPwMsg('');
    if (pw.next.length < 8) { setPwErr('New password must be at least 8 characters'); return; }
    if (pw.next !== pw.confirm) { setPwErr('New passwords do not match'); return; }
    setPwBusy(true);
    try {
      await api.post('/admin/change-password', { current_password: pw.current, new_password: pw.next });
      setPwMsg('Password updated. You will stay signed in.');
      setPw({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwMsg(''), 3500);
    } catch (e) { setPwErr(formatApiError(e)); } finally { setPwBusy(false); }
  };

  if (loading) return <div className="admin-loading"><i className="fa-solid fa-spinner fa-spin"></i><span>Loading…</span></div>;

  return (
    <div>
      <div className="admin-page-head">
        <div><h1>Settings</h1><p className="muted">Store info & admin password</p></div>
      </div>

      {/* Store settings */}
      <form onSubmit={save} className="admin-form-grid" data-testid="store-settings-form">
        <section className="admin-card">
          <h2 className="admin-card-h">Store info</h2>

          <label><span>Store name</span><input type="text" value={store.name} onChange={e => setStore(s => ({ ...s, name: e.target.value }))} required data-testid="settings-name" /></label>
          <div className="grid-2">
            <label><span>Phone (display)</span><input type="text" value={store.phone} onChange={e => setStore(s => ({ ...s, phone: e.target.value }))} required data-testid="settings-phone" /></label>
            <label><span>Phone (tel: format)</span><input type="text" value={store.phone_raw} onChange={e => setStore(s => ({ ...s, phone_raw: e.target.value }))} required placeholder="+13479615885" data-testid="settings-phone-raw" /></label>
          </div>
          <div className="grid-2">
            <label><span>WhatsApp (digits only)</span><input type="text" value={store.whatsapp} onChange={e => setStore(s => ({ ...s, whatsapp: e.target.value }))} required placeholder="13479615885" data-testid="settings-whatsapp" /></label>
            <label><span>Email</span><input type="email" value={store.email} onChange={e => setStore(s => ({ ...s, email: e.target.value }))} data-testid="settings-email" /></label>
          </div>
          <label><span>Address</span><input type="text" value={store.address} onChange={e => setStore(s => ({ ...s, address: e.target.value }))} required data-testid="settings-address" /></label>
        </section>

        <section className="admin-card">
          <h2 className="admin-card-h">Hours</h2>
          {DAYS.map(({ d, label }) => (
            <label key={d} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span style={{ textTransform: 'none', letterSpacing: 0 }}>{label}</span>
              <input
                type="text"
                value={store.hours[d] || ''}
                onChange={e => setStore(s => ({ ...s, hours: { ...s.hours, [d]: e.target.value } }))}
                placeholder="9:00 AM – 7:00 PM"
                data-testid={`settings-hours-${d}`}
              />
            </label>
          ))}
        </section>

        {err && <div className="form-error">{err}</div>}
        {msg && <div className="review-success" data-testid="settings-saved">{msg}</div>}

        <div className="admin-form-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={saving} data-testid="settings-save-btn">
            <i className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'}></i>
            <span>{saving ? 'Saving…' : 'Save changes'}</span>
          </button>
        </div>
      </form>

      {/* Password change */}
      <form onSubmit={changePassword} className="admin-form-grid" style={{ marginTop: 24 }} data-testid="password-form">
        <section className="admin-card">
          <h2 className="admin-card-h">Change admin password</h2>
          <label><span>Current password</span><input type="password" required value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} data-testid="settings-pw-current" /></label>
          <div className="grid-2">
            <label><span>New password</span><input type="password" required value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} data-testid="settings-pw-new" /></label>
            <label><span>Confirm new password</span><input type="password" required value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} data-testid="settings-pw-confirm" /></label>
          </div>
          {pwErr && <div className="form-error">{pwErr}</div>}
          {pwMsg && <div className="review-success">{pwMsg}</div>}
          <div className="admin-form-actions">
            <button type="submit" className="btn btn-primary btn-lg" disabled={pwBusy} data-testid="settings-pw-save-btn">
              <i className={pwBusy ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-key'}></i>
              <span>{pwBusy ? 'Updating…' : 'Update password'}</span>
            </button>
          </div>
        </section>
      </form>
    </div>
  );
}
